import { IonContent, IonPage } from '@ionic/react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { IState, IUserInfo } from 'appRedux/createStore';
import EmailSettingRow from './EmailSettingRow';
import ResetSettingsModal from './ResetSettingsModal';
import { SettingRow, SettingsToggle, formatPhone } from './SettingRow';
import SettingsHeader from './SettingsHeader';
import {
  APP_DEFAULT_KEYS,
  SETTING_DEFINITIONS,
  SettingOption,
  isSameSettingValue,
} from './settingsOptions';
import { useCasinoOptions } from './useCasinoOptions';
import { anyVisible } from './settingsVisibility';
import { useUserSettings } from './useUserSettings';

const Settings: React.FC = () => {
  const history = useHistory();
  const { phone } = useSelector<IState>((state) => state.app.persistedState.userInfo) as IUserInfo;
  const { settings, defaults, updateSetting, resetSettings, visibility } = useUserSettings({
    refresh: true,
  });
  const casinoOptions = useCasinoOptions();
  const [showResetModal, setShowResetModal] = useState(false);

  const valueLabel = (key: (typeof APP_DEFAULT_KEYS)[number]) => {
    if (!settings) return '';
    if (key === 'compareInsights') {
      return isSameSettingValue(settings.compareInsights, defaults?.compareInsights)
        ? 'Default'
        : 'Custom';
    }
    const options: SettingOption[] = [
      ...SETTING_DEFINITIONS[key].options,
      ...(key === 'openingCasino' ? casinoOptions : []),
    ];
    const option = options.find(({ value }) => value === settings[key]);
    return option ? option.shortLabel ?? option.label : '';
  };

  const { accountInfo, notifications, appDefaults, restoreDefaults } = visibility.settings;
  const appDefaultKeys = APP_DEFAULT_KEYS.filter((key) => appDefaults[key]);

  return (
    <IonPage className="settings-page">
      <SettingsHeader />
      <IonContent className="settings-content">
        {anyVisible(accountInfo) && (
          <section className="settings-group">
            <h2 className="settings-group__title">Account Info</h2>
            <div className="settings-card">
              {accountInfo.phone && <SettingRow label="Phone Number" value={formatPhone(phone)} />}
              {accountInfo.email && <EmailSettingRow />}
              {accountInfo.changePassword && (
                <SettingRow
                  label="Change password"
                  onClick={() => history.push('/changepassword')}
                />
              )}
            </div>
          </section>
        )}

        {anyVisible(notifications) && (
          <section className="settings-group">
            <h2 className="settings-group__title">Notifications</h2>
            <div className="settings-card">
              {notifications.allOnOff && (
                <SettingRow label="All ON/OFF">
                  <SettingsToggle
                    checked={!!settings?.notificationsEnabled}
                    label="All notifications"
                    disabled={!settings}
                    onChange={(checked) => updateSetting('notificationsEnabled', checked)}
                  />
                </SettingRow>
              )}
              {notifications.editNotifications && (
                <SettingRow
                  label="Edit Notifications"
                  value="Custom"
                  onClick={() => history.push('/settings/notifications')}
                />
              )}
            </div>
          </section>
        )}

        {appDefaultKeys.length > 0 && (
          <section className="settings-group">
            <h2 className="settings-group__title">App Defaults</h2>
            <div className="settings-card">
              {appDefaultKeys.map((key) => (
                <SettingRow
                  key={key}
                  label={SETTING_DEFINITIONS[key].title}
                  value={valueLabel(key)}
                  onClick={() => history.push(`/settings/${key}`)}
                />
              ))}
            </div>
          </section>
        )}

        {restoreDefaults && (
          <button
            className="settings-card settings-action"
            disabled={!settings}
            onClick={() => setShowResetModal(true)}
          >
            <span className="settings-action__label">Restore Defaults</span>
          </button>
        )}
      </IonContent>

      {showResetModal && (
        <ResetSettingsModal
          onConfirm={() => {
            setShowResetModal(false);
            resetSettings();
          }}
          onClose={() => setShowResetModal(false)}
        />
      )}
    </IonPage>
  );
};

export default Settings;
